import { useContext, useEffect, useState } from 'react'

import { Box } from '@fairfashion/design-system'
import {
  Background,
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow'

import 'reactflow/dist/style.css'
import Switch from '../../../../components/Switch'
import { TraceabilityTreeProps } from '../../../../components/TraceabilityTree/index.types'
import styles from '../../../../components/TraceabilityTree/styles.module.scss'
import { SegmentsDataProvider } from '../../../../contexts/segments'
import { OriginItemsContext } from '../../origin-items.context'

const CreateItemTraceabilityTreeComp = ({
  showDirectionSwitch = true,
  showMinimap = true,
  initialDirection,
}: TraceabilityTreeProps) => {
  const [loaded, setLoaded] = useState(false)

  const { nodes, edges, nodeTypes, actions, direction } =
    useContext(OriginItemsContext)

  const { fitView } = useReactFlow()

  const setCenterToRoot = () => {
    const centerNodes = nodes.slice(0, 10)

    fitView({ nodes: [...centerNodes], maxZoom: 1, minZoom: 0.2 })
  }

  const loadDirection = () => {
    if (!showDirectionSwitch && initialDirection) {
      actions.setDirection(initialDirection)
      return
    }

    if (initialDirection) {
      actions.setDirection(initialDirection)
    }

    const userDirection = localStorage.getItem('tree-direction')
    if (userDirection) {
      actions.setDirection(userDirection as 'LR' | 'TB')
    }
  }

  useEffect(() => {
    loadDirection()
  }, [])

  useEffect(() => {
    if (loaded) return
    // This interval is used to guarantee that nodes will be rendered correctly
    const interval = setInterval(() => {
      const element: HTMLDivElement | null = document.querySelector(`[data-id]`)

      // The second child element is the one that contains the nodes
      const secondChildElement = element?.children[1] as HTMLDivElement

      // Set the width and height of the dagre node to match the custom node
      actions.setNodeWidth(secondChildElement?.offsetWidth ?? 0)
      actions.setNodeHeight(secondChildElement?.offsetHeight ?? 0)

      // If the second child element exists, it means that the nodes have been rendered
      if (secondChildElement) {
        actions.loadLayout()
        clearInterval(interval)
      }
    }, 100)
  }, [actions.loadLayout, fitView])

  useEffect(() => {
    if (!loaded && nodes?.length) {
      setTimeout(() => {
        setLoaded(true)
        loadDirection()
        setCenterToRoot()
      }, 1000)
    }
  }, [loaded, nodes])

  const toggleDirection = () => {
    const newDirection = direction === 'LR' ? 'TB' : 'LR'
    actions.setDirection(newDirection)
    localStorage.setItem('tree-direction', newDirection)
    actions.loadLayout()
    setTimeout(() => {
      setCenterToRoot()
    }, 500)
  }

  return (
    <div
      className={styles.container}
      style={{
        opacity: loaded ? 1 : 0,
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
      >
        <Background color="#666" gap={16} />
        <Controls showInteractive={false} />
        {showMinimap && <MiniMap pannable className={styles.minimap} />}
        {showDirectionSwitch && (
          <Panel position="top-right">
            <Box gap={1} alignItems="center">
              <div className={styles.topPanel}>
                <Switch
                  textLeft="Horizontal"
                  textRight="Vertical"
                  isSkuChecked={direction === 'TB'}
                  handleChange={toggleDirection}
                />
              </div>
            </Box>
          </Panel>
        )}
      </ReactFlow>
    </div>
  )
}

export const CreateItemTraceabilityTree = (props: TraceabilityTreeProps) => {
  return (
    <ReactFlowProvider>
      <SegmentsDataProvider>
        <CreateItemTraceabilityTreeComp {...props} />
      </SegmentsDataProvider>
    </ReactFlowProvider>
  )
}
